import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEmailmarketingApiV1SenderDetailsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/shoutout',
        destPath: '',
      },
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/shoutout',
        destPath: '',
      },
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/shoutout',
        destPath: '',
      },
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'shoutout._base_domain_': [
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'shoutouteditor._base_domain_': [
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/shoutout-editor',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'api._base_domain_': [
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
      {
        srcPath: '/_api/shoutout/v1',
        destPath: '/v1',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/email-marketing/v1',
        destPath: '/v1',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/shoutout-editor/v1',
        destPath: '/v1',
      },
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/shoutout-editor/v1',
        destPath: '/v1',
      },
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/shoutout-editor/v1',
        destPath: '/v1',
      },
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
    ],
    'payments.base44.com': [
      {
        srcPath: '/_api/shoutout-editor',
        destPath: '/api',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_email-marketing_sender-details';

/**
 * Retrieves sender details.
 *
 * The sender details include the information displayed as the email sender's name and email address.
 *
 * + If you send an email campaign with a public email domain (e.g. @gmail.com or @yahoo.com), the email address will not be displayed in the 'from' header. Instead, the email address is replaced with @wixemails.com (or @wixsitemail.com for free users), and the sender's email is placed in the 'reply-to' header.
 * + If you send an email campaign with an email from a Wix managed domain, that email will be displayed in the 'from' header.
 * + If you send an email from a custom domain that is not managed by Wix, a line will be inserted that states that the email was sent via wixemails.com.
 * @deprecated It has been replaced with wix.promote.v1.SenderDetailsService.GetDefaultSenderDetails(), and will be removed on 2025-09-30.
 */
export function get(payload: object): RequestOptionsFactory<any> {
  function __get({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.email_marketing.v1.sender_details',
      method: 'GET' as any,
      methodFqn: 'wix.emailmarketing.api.v1.SenderDetailsService.Get',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailmarketingApiV1SenderDetailsServiceUrl({
        protoPath: '/v1/sender-details',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'senderDetails.dateVerified' }],
          },
        ]),
    };

    return metadata;
  }

  return __get;
}

/**
 * Updates sender details (only provided fields will be updated).
 *
 * If `from_email` is changed - a verification code will be sent to new email address.
 * If verification is needed, a verification email will be sent to the user, and `verification_needed` will return true.
 * @deprecated It has been replaced with wix.promote.v1.SenderEmailService.GetOrCreateSenderEmail(), and will be removed on 2025-09-30.
 */
export function update(payload: object): RequestOptionsFactory<any> {
  function __update({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'senderDetails.dateVerified' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.email_marketing.v1.sender_details',
      method: 'PATCH' as any,
      methodFqn: 'wix.emailmarketing.api.v1.SenderDetailsService.Update',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailmarketingApiV1SenderDetailsServiceUrl({
        protoPath: '/v1/sender-details',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __update;
}

/**
 * Verifies the sender's email using a verification code sent to the user's email address upon update.
 * @deprecated It has been replaced with wix.promote.v1.SenderEmailService.VerifySenderEmail(), and will be removed on 2025-09-30.
 */
export function verifyEmail(payload: object): RequestOptionsFactory<any> {
  function __verifyEmail({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.email_marketing.v1.sender_details',
      method: 'POST' as any,
      methodFqn: 'wix.emailmarketing.api.v1.SenderDetailsService.VerifyEmail',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailmarketingApiV1SenderDetailsServiceUrl({
        protoPath: '/v1/sender-details/verify-email',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __verifyEmail;
}

/**
 * Checks if the sender's email address will be used as from-address or replaced (not related to current sender details).
 * @deprecated It has been replaced with wix.emailmarketing.api.v1.CampaignService.IdentifySenderAddress(), and will be removed on 2025-09-30.
 */
export function resolveActualFromAddress(
  payload: object
): RequestOptionsFactory<any> {
  function __resolveActualFromAddress({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.email_marketing.v1.sender_details',
      method: 'POST' as any,
      methodFqn:
        'wix.emailmarketing.api.v1.SenderDetailsService.ResolveActualFromAddress',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailmarketingApiV1SenderDetailsServiceUrl({
        protoPath: '/v1/sender-details/actual-from-address',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __resolveActualFromAddress;
}
